import { useMemo, useCallback } from 'react';
import { ChartData } from '../types';

// Format date to string for comparison
const formatDateKey = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

interface UseChartDataProps {
  chartData: ChartData[];
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  parsedDates: Date[];
}

interface ChartDataResult {
  processedChartData: ChartData[];
  allDates: string[];
  getDatePosition: (date: string) => number;
}

export const useChartData = ({
  chartData,
  dateRange,
  parsedDates
}: UseChartDataProps): ChartDataResult => {
  // Generate a complete series of dates for the x-axis
  const fullDateRange = useMemo(() => {
    const { startDate, endDate } = dateRange;
    if (!startDate || !endDate) return [];
    
    const dates: Date[] = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  }, [dateRange]);

  // Enrich data with all dates in the range
  const enrichedChartData = useMemo(() => {
    if (fullDateRange.length === 0 || !dateRange.startDate || !dateRange.endDate) return chartData;

    // Group chart data entries by their date portion (YYYY-MM-DD)
    // This handles both date-only keys (YYYY-MM-DD) and datetime keys (YYYY-MM-DDTHH:MM)
    const dataByDay = new Map<string, ChartData[]>();
    chartData.forEach(item => {
      const formattedDate = item.FormattedDate || '';
      const dayKey = formattedDate.split('T')[0];
      const parsedDate = new Date(formattedDate);

      // Only include data within the selected range
      if (parsedDate >= dateRange.startDate! && parsedDate <= dateRange.endDate!) {
        if (!dataByDay.has(dayKey)) dataByDay.set(dayKey, []);
        dataByDay.get(dayKey)!.push(item);
      }
    });

    // Build base data: for each day in range, include existing entries or create empty point
    const baseData = fullDateRange.flatMap(date => {
      const key = formatDateKey(date);
      const entries = dataByDay.get(key);

      if (entries && entries.length > 0)
        return entries;

      // Create empty data point for days with no data
      const emptyData: any = {
        FormattedDate: key,
        ShortDate: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      };

      // Add null values for all metrics
      chartData.forEach(item => {
        Object.keys(item).forEach(k => {
          if (k !== 'ShortDate' && k !== 'FormattedDate')
            emptyData[k] = null;
        });
      });

      return [emptyData];
    });

    // Sort and deduplicate by full FormattedDate (preserves datetime keys)
    const sorted = baseData.sort(
      (a, b) => new Date(a.FormattedDate).getTime() - new Date(b.FormattedDate).getTime()
    );
    const seen = new Set<string>();
    return sorted.filter(item => {
      const key = item.FormattedDate;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [fullDateRange, parsedDates, dateRange, chartData]);

  // Use the enriched data as filtered data
  const filteredChartData = useMemo(() => enrichedChartData, [enrichedChartData]);

  // Process chart data (convert weights)
  const processedChartData = useMemo(() => {
    return filteredChartData.map(dataPoint => {
      const newDataPoint = { ...dataPoint };
      return newDataPoint;
    });
  }, [filteredChartData]);

  // Extract all dates for timeline reference
  const allDates = useMemo(() => 
    filteredChartData.map(d => d.FormattedDate || d.ShortDate), 
    [filteredChartData]
  );

  // Calculate position for a date on the timeline
  const getDatePosition = useCallback((date: string) => {
    const dateIndex = allDates.indexOf(date);
    
    if (dateIndex < 0) {
      const inputDate = new Date(date);
      const lastDate = new Date(allDates[allDates.length - 1]);
      
      return inputDate > lastDate ? 100 : 0;
    }
    
    return dateIndex * (100 / (allDates.length - 1));
  }, [allDates]);

  return {
    processedChartData,
    allDates,
    getDatePosition
  };
}; 